// Bilingual case-narrative hooks.
//
// The narrative is the AI-generated PT prose body (with parallel EN
// translation) that backs the firm-message PDF. The UI shows BOTH
// languages so the expat ICP can read what they're about to send before
// they download the PT-only PDF.
//
// Read-side (`useNarrative`) is cache-first: server lazy-upserts on hash
// drift but a warm read is one SELECT — safe to mount in the case page.
//
// Refresh-side (`useRefreshNarrative`) forces an LLM regen. Wire it to a
// "Refresh" button in the preview UI; the button is rate-limited at the
// API (10/hr/user) so spam-clicking is harmless.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api';
import { useMe } from './auth';

export type CaseNarrative = {
  caseId: string;
  pt_paragraphs: string[];
  en_paragraphs: string[];
  /** Explicit "Pedido" sentence rendered alongside the narrative in the
   * preview modal. PT is the deterministic ask sourced from the funnel;
   * EN is the LLM's translation, kept aligned. */
  pedido_pt: string;
  pedido_en: string;
  source_hash: string;
  model: string;
  prompt_version: string;
  event_count_at_generation: number;
  generated_at: string;
};

export type LetterParties = {
  owner: string;
  contractor: string;
  location: string;
};

export type LetterBundle = {
  narrative: CaseNarrative | null;
  parties: LetterParties | null;
  amount_paid_eur: number | null;
};

export const narrativeQueryKey = (caseId: string) => ['cases', caseId, 'narrative'] as const;

export function useNarrative(caseId: string | undefined) {
  const me = useMe();
  return useQuery({
    queryKey: narrativeQueryKey(caseId ?? ''),
    queryFn: () => apiFetch<LetterBundle>(`/cases/${caseId}/narrative`),
    enabled: !!caseId && !!me.data?.user,
    // Server-side ensure is idempotent for unchanged inputs (hash compare),
    // so a quick refetch is cheap. Don't poll, but let staleness settle.
    staleTime: 30_000,
  });
}

export function useRefreshNarrative(caseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<LetterBundle>(`/cases/${caseId}/narrative/refresh`, {
        method: 'POST',
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: narrativeQueryKey(caseId) }),
  });
}
